
<template>
    <Entity />
</template>

<script setup>

/**
 * This component is a show more component that decides based upon the threshold, whether some of the children
 * should be displayed in a show more container.  The decision is based upon the 'threshold' property.  If there
 * are more children than the threshold, some of the children will be placed in an expand/collapse container.
 *
 * Usage
 *
 * <ShowMore lopLength="2" threshold="10" :key="myKey">
 *    <div><input type="checkbox"><label>My label</label></div>
 *    <div><input type="checkbox"><label>My label</label></div>
 * </ShowMore>
 *
 * Properties:
 *
 * threshold:  The number of  child items that triggers the usage of the expand/collapse element.
 * topLength:  The number of items to display at the top ( above the show more element )
 * key:  Make sure to supply a key that indicates when the data changes.  If we don't supply a key, then the children will get re-rendered,
 * and the component will throw DOM errors as the DOM state will get confused.  The solution is to supply a key so that the component gets
 * fully re-rendered each time the data changes.  One way to do this is to supply computed property that does a JSON.stringify() on the data
 * being rendered as children.
 */

  //The threshold ( # of items that causes the expand/collapse to display )
    const props = defineProps({
        threshold: {
        type: Number,
        default: 8,
        },
        topLength: {
        type: Number,
        default: 4,
        },
    })

    const showMore = ref(false)
    const buttonLabel = computed(() => (showMore.value ? 'Show less' : 'Show more'))
    const iconName = computed(() => (showMore.value ? 'system-hide' : 'system-show'))
    const slots = useSlots()

    //this is the pane ( div ) that contains the items to be shown by the 'show more' button.
    const moreItemsId = computed(() => `more-items${new Date().getTime()}`)

    const onButtonClick = (e) => {
        //console.log("OnCLick fired!!")
      e.preventDefault()
      showMore.value = !showMore.value
    }

    const onKeyup = (e) => {
      //console.log('key-up', e)
      if (e.code == 'Escape' && showMore.value == true) {
        showMore.value = !showMore.value
      }
    }

    const render = () => {

        if (!slots.default()) return ''

        //For some reason we get a bunch of text nodes along with the stuff we care about.  Strip out the text nodes.
        //const children = this.$slots.default.length > 0 ? this.$slots.default.filter((n) => n.text != ' ') : []
        const children = slots.default()[0].children

        //Draw the component with 'all' items and no 'Show More' button.
        const all = () => {
            return h('div', children)
        }

        //Draw the component with the 'More' items.
        const withShowMore = () => {
            const elements = []

            for (let i = 0; i < props.topLength; i++) {
                elements.push(children[i])
            }

            //Draw the items in the 'More items' pane
            const inDropDown = []
            for (let i = props.topLength; i < children.length; i++) {
                inDropDown.push(children[i])
            }

            //Create the button element
            const icon = h('hp5-icon', { name: iconName.value })
            const span = h('span', buttonLabel.value )
            const button = h(
                'button',
                {
                    onClick(event) { //console.log("Button click", self)
                        onButtonClick(event)
                    },
                    'aria-expanded': showMore.value ? true : false,
                },
                [icon, span]
            )

            //Surround the button in a hp5-button-decorator
            const decorator = h(
                'hp5-button-decorator',
                {
                    type: 'tertiary',
                    size: 'md'
                },
                [button]
            )

            elements.push(decorator)

            //If we should be showing more, draw the elements in the moreItems panel.
            if (showMore.value) {
                const morePane = h(
                'div',
                {
                    id: moreItemsId.value
                },
                [inDropDown]
            )
            elements.push(morePane)
        }

        return h('div', elements)
    }

    if ( !children ) return ''

    return children && children.length < props.threshold ? all() : withShowMore()
  }

  const Entity = computed(() => render() )

</script>

<style scoped>
hp5-button-decorator {
  display: block;
  padding-top: var(--spacing-xxs);
  padding-bottom: var(--spacing-xxs);
}
</style>